<template>
	<div class="lightbox-overlay" role="dialog" aria-modal="true" :aria-label="fileName" @click.self="close">
		<div class="lightbox-content">
			<div class="lightbox-toolbar">
				<div class="lightbox-title">
					<span class="lightbox-file-icon"><portal-icon :name="isPdf ? 'file-text' : 'eye'" :size="18" /></span>
					<span>{{ fileName }}</span>
				</div>
				<button class="close-button" type="button" :aria-label="$t('global.close')" @click="close">
					<portal-icon name="x" :size="20" />
				</button>
			</div>
			<div v-if="isPdf" class="pdf-container">
				<iframe :src="url" :title="fileName"></iframe>
			</div>
			<div v-else class="image-container">
				<img :src="url" :alt="fileName" />
			</div>
		</div>
	</div>
</template>

<script>
import PortalIcon from '@/components/PortalIcon';

export default {
	name: "LightBox",
	components: { PortalIcon },
	props: {
		url: {
			type: String,
			required: true
		},
		fileName: {
			type: String,
			required: true
		}
	},
	mounted() {
		window.addEventListener('keydown', this.handleKeydown);
	},
	beforeDestroy() {
		window.removeEventListener('keydown', this.handleKeydown);
	},
	computed: {
		// Checks if the URL ends with '.pdf' (case insensitive)
		isPdf() {
			return this.fileName.toLowerCase().endsWith('.pdf');
		}
	},
	methods: {
		handleKeydown(event) {
			if (event.key === 'Escape') {
				this.close();
			}
		},
		close() {
			this.$emit("close");
		}
	}
};
</script>

<style scoped lang="scss">
.lightbox-overlay {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100vh;
	min-width: 100vw;
	min-height: 100vh;
	padding: 24px;
	background: rgba(13, 14, 20, 0.88);
	backdrop-filter: blur(10px);
	display: flex;
	justify-content: center;
	align-items: center;
	z-index: 10000;
}

.lightbox-content {
	position: relative;
	width: min(1320px, 100%);
	height: min(900px, 100%);
	background: #171820;
	overflow: hidden;
	border: 1px solid rgba(255, 255, 255, 0.14);
	border-radius: var(--brand-surface-radius, 22px);
	box-shadow: 0 28px 90px rgba(0, 0, 0, 0.42);
	display: flex;
	flex-direction: column;
}

.lightbox-toolbar {
	min-height: 64px;
	padding: 10px 12px 10px 18px;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 14px;
	background: rgba(24, 25, 34, 0.96);
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
	color: white;
}

.lightbox-title {
	min-width: 0;
	display: flex;
	align-items: center;
	gap: 11px;
	font-size: 14px;
	font-weight: 600;

	span:last-child {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}
}

.lightbox-file-icon {
	width: 36px;
	height: 36px;
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border-radius: 11px;
	background: rgba(255, 255, 255, 0.1);
	color: rgba(255, 255, 255, 0.88);
}

.pdf-container {
	width: 100%;
	height: auto;
	flex: 1 1 auto;
	min-height: 0;
	min-height: 100%;
	min-width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}

.image-container {
	max-height: 100%;
	max-width: 100%;
	height: auto;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
}

.image-container img {
	max-width: 100%;
	max-height: 100%;
	width: auto;
	height: auto;
	display: block;
}

.pdf-container iframe {
	display: block;
	width: 100%;
	height: 100%;
	min-height: 100%;
	min-width: 100%;
}

.close-button {
	width: 40px;
	height: 40px;
	flex: 0 0 auto;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	border: 1px solid rgba(255, 255, 255, 0.13);
	border-radius: var(--brand-button-radius, 50%);
	background: rgba(255, 255, 255, 0.08);
	color: white;
	cursor: pointer;

	&:hover {
		background: rgba(255, 255, 255, 0.15);
	}
}

@media (max-width: 600px) {
	.lightbox-overlay {
		padding: 0;
	}

	.lightbox-content {
		width: 100%;
		height: 100%;
		border: 0;
		border-radius: 0;
	}
}
</style>
